import React, { useState, useEffect, useRef } from 'react';
import './style.scss';

const PREFIX = 'music';

export interface DateTimePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  disabledDate?: (date: Date) => boolean;
  className?: string;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select time',
  disabledDate,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(value || new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(
    value ? `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}` : null
  );

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    };
    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker]);

  // 生成日历数据
  const generateCalendar = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: { date: Date; isCurrentMonth: boolean }[] = [];
    
    // 上个月的日期
    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthDay = new Date(year, month, -startingDayOfWeek + i + 1);
      days.push({ date: prevMonthDay, isCurrentMonth: false });
    }

    // 当前月的日期
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    return days;
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleDateSelect = (date: Date) => {
    const newDate = new Date(date);
    if (selectedTime) {
      const [hours, minutes] = selectedTime.split(':');
      newDate.setHours(parseInt(hours), parseInt(minutes));
    } else {
      newDate.setHours(12, 0); // 默认中午12点
    }
    onChange?.(newDate);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (value) {
      const [hours, minutes] = time.split(':');
      const newDate = new Date(value);
      newDate.setHours(parseInt(hours), parseInt(minutes));
      onChange?.(newDate);
    }
  };

  // 时间选项（每15分钟）
  const timeSlots: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      timeSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  const isDateDisabled = (date: Date) => {
    if (disabledDate) {
      return disabledDate(date);
    }
    // 默认禁用今天之前的日期
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  };

  return (
    <div className={`${PREFIX}-datetime-picker-container ${className || ''}`} ref={containerRef}>
      <div 
        className={`${PREFIX}-datetime-picker__input`}
        onClick={() => setShowPicker(!showPicker)}
      >
        <span className={value ? `${PREFIX}-datetime-picker__value` : `${PREFIX}-datetime-picker__placeholder`}>
          {value 
            ? value.toLocaleString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : placeholder
          }
        </span>
        <svg className={`${PREFIX}-datetime-picker__icon`} width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
        </svg>
      </div>

      {/* 日期时间选择弹窗 */}
      {showPicker && (
        <div className={`${PREFIX}-datetime-picker__dropdown`}>
          {/* 日历 */}
          <div className={`${PREFIX}-datetime-picker__calendar`}>
            {/* 月份导航 */}
            <div className={`${PREFIX}-datetime-picker__header`}>
              <button
                type="button"
                onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
                className={`${PREFIX}-datetime-picker__nav-btn`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </button>
              <span className={`${PREFIX}-datetime-picker__month`}>{formatMonthYear(selectedMonth)}</span>
              <button
                type="button"
                onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
                className={`${PREFIX}-datetime-picker__nav-btn`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </button>
            </div>

            {/* 星期标题 */}
            <div className={`${PREFIX}-datetime-picker__weekdays`}>
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className={`${PREFIX}-datetime-picker__weekday`}>{day}</div>
              ))}
            </div>

            {/* 日期网格 */}
            <div className={`${PREFIX}-datetime-picker__days`}>
              {generateCalendar().map((day, index) => {
                const isToday = day.date.toDateString() === new Date().toDateString();
                const isSelected = value && day.date.toDateString() === value.toDateString();
                const isPast = isDateDisabled(day.date);
                
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => day.isCurrentMonth && !isPast && handleDateSelect(day.date)}
                    className={`${PREFIX}-datetime-picker__day ${
                      !day.isCurrentMonth ? `${PREFIX}-datetime-picker__day--outside` : ''
                    } ${isSelected ? `${PREFIX}-datetime-picker__day--selected` : ''} ${
                      isToday ? `${PREFIX}-datetime-picker__day--today` : ''
                    } ${isPast && day.isCurrentMonth ? `${PREFIX}-datetime-picker__day--disabled` : ''}`}
                    disabled={!day.isCurrentMonth || isPast}
                  >
                    {day.date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 时间列表 */}
          <div className={`${PREFIX}-datetime-picker__time`}>
            <div className={`${PREFIX}-datetime-picker__time-header`}>Time</div>
            <div className={`${PREFIX}-datetime-picker__time-list`}>
              {timeSlots.map(time => (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleTimeSelect(time)}
                  className={`${PREFIX}-datetime-picker__time-item ${selectedTime === time ? `${PREFIX}-datetime-picker__time-item--selected` : ''}`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
